// Payments/PaymentsPage.js

import React, { useState, useEffect } from 'react';
import axiosInstance from "../../utils/axiosInstance";
import {
  Box, Typography, Tabs, Tab, Paper, Card, CardContent, Container, 
  useTheme, alpha, Stack, Divider
} from '@mui/material';
import BalanceDisplay from './BalanceDisplay';
import CustomerPaymentsTable from './CustomerPaymentsTable';
import WithdrawalHistoryTable from './WithdrawalHistoryTable';
import PaymentLinksPage from './PaymentLinksPage';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LinkIcon from '@mui/icons-material/Link';
import PaymentIcon from '@mui/icons-material/Payment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const PaymentsPage = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  
  const handleRefresh = () => setRefreshKey(prev => prev + 1);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Paper sx={{ p: 3, mt: 10, mx: 'auto', maxWidth: '1200px', bgcolor: 'background.paper', height: '100%' }}>
            
                
        <Typography variant="h4" component="h1" sx={{ mb: 4, fontWeight: 700 }} color="textPrimary">
          Manage Payments
        </Typography>
        <Divider sx={{ mb: 3, borderColor: "divider" }} />
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          {/* Left Column (Tabs) */}
        <Paper
          elevation={2}
          sx={{
            minWidth: { md: '220px' },
            height: 'fit-content',
            position: { md: 'sticky' },
            top: { md: 20 },
            bgcolor: 'background.paper',
          }}
        >
           <Tabs
            value={tabValue}
            onChange={handleTabChange}
            orientation="vertical"
            variant="scrollable"
            TabIndicatorProps={{
              style: {
                backgroundColor: theme.palette.primary.main,
                width: '4px',
                left: 0,
              }
            }}
            sx={{
              '& .MuiTab-root': {
                alignItems: 'flex-start',
                textAlign: 'left',
                px: 3,
                py: 2,
                justifyContent: 'flex-start',
                color: `${theme.palette.text.primary} !important`,
                transition: 'all 0.2s ease',
                minHeight: 48,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '& .MuiSvgIcon-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiTab-wrapper': {
                  color: theme.palette.text.primary,
                },
              },
              '& .MuiTab-root.Mui-selected': {
                color: `${theme.palette.primary.main} !important`,
                backgroundColor: theme.palette.action.selected,
                fontWeight: 'bold',
                '& .MuiSvgIcon-root': {
                  color: theme.palette.primary.main,
                },
                '& .MuiTab-wrapper': {
                  color: theme.palette.primary.main,
                },
              },
            }}
          >
            <Tab 
              icon={<DashboardIcon sx={{ color: 'inherit' }} />} 
              iconPosition="start" 
              label="Overview" 
              color='textPrimary'
            />
            <Tab 
              icon={<LinkIcon sx={{ color: 'inherit' }} />} 
              iconPosition="start" 
              label="Manage Payment Links" 
              sx={{ color: 'inherit' }}
            />
            <Tab 
              icon={<PaymentIcon sx={{ color: 'inherit' }} />} 
              iconPosition="start" 
              label="Customer Payments" 
              sx={{ color: 'inherit' }}
            />
            <Tab 
              icon={<AccountBalanceIcon sx={{ color: 'inherit' }} />} 
              iconPosition="start" 
              label="Withdrawal History" 
              sx={{ color: 'inherit' }}
            />
          </Tabs>
        </Paper>

          {/* Right Column (Content) */}
          <Box sx={{ flex: 1, minWidth: 0 }}> {/* FIX: Added minWidth: 0 */}
            {tabValue === 0 && <OverviewTab refreshKey={refreshKey} onRefresh={handleRefresh} />}
            {tabValue === 1 && <PaymentLinksTab refreshKey={refreshKey} />}
            {tabValue === 2 && <CustomerPaymentsTab refreshKey={refreshKey} />}
            {tabValue === 3 && <WithdrawalTransactionsTab refreshKey={refreshKey} />}
          </Box>
        </Box>
    </Paper>
  );
};

const OverviewTab = ({ refreshKey, onRefresh }) => {
  const theme = useTheme();
  const [summary, setSummary] = useState({ last_week: 0, last_month: 0, last_year: 0 });

  useEffect(() => {
    const controller = new AbortController();

    axiosInstance
      .get(`/payments/summary`, { signal: controller.signal })
      .then((res) => setSummary(res.data.summary))
      .catch((err) => {
        if (err.name !== "CanceledError") {
          console.error("Failed to fetch summary", err);
        }
      });

    return () => controller.abort();
  }, [refreshKey]);

  return (
    <Paper elevation={3} sx={{ p: 4 }}>
      <BalanceDisplay onWithdrawalSuccess={onRefresh} refreshKey={refreshKey} />

      {/* Summary Cards */}
      <Box sx={{ my: 4 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
          <TrendingUpIcon color="primary" />
          <Typography variant="h6" fontWeight={600}  color="textPrimary">Revenue Summary</Typography>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
          <Card sx={{ 
            flex: 1, 
            minWidth: '150px',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
          }}>
            <CardContent>
              <Typography variant="body2" color="textPrimary" sx={{ mb: 1, fontWeight: 500 }}>
                Last Week
              </Typography>
              <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
                ${Number(summary.last_week).toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ 
            flex: 1, 
            minWidth: '150px',
            background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`
          }}>
            <CardContent>
              <Typography variant="body2" color="textPrimary" sx={{ mb: 1, fontWeight: 500 }}>
                Last Month
              </Typography>
              <Typography variant="h4" color="secondary" sx={{ fontWeight: 700 }}>
                ${Number(summary.last_month).toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ 
            flex: 1, 
            minWidth: '150px',
            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)} 0%, ${alpha(theme.palette.success.main, 0.02)} 100%)`,
            border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`
          }}>
            <CardContent>
              <Typography variant="body2" color="textPrimary" sx={{ mb: 1, fontWeight: 500 }}>
                Last Year
              </Typography>
              <Typography variant="h4" color="success.main" sx={{ fontWeight: 700 }} >
                ${Number(summary.last_year).toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Stack>
      </Box>
      
      {/* Recent Payments */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}  color="textPrimary">
          Recent Payments
        </Typography>
        <CustomerPaymentsTable perPage={5} showPagination={false} refreshKey={refreshKey} />
      </Box>
      
      {/* Recent Withdrawals */}
      <Box>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}  color="textPrimary">
          Recent Withdrawals
        </Typography>
        <WithdrawalHistoryTable 
          perPage={5} 
          showPagination={false} 
          refreshKey={refreshKey} 
        />
      </Box>
    </Paper>
  );
};

const CustomerPaymentsTab = ({ refreshKey }) => (
  <Paper elevation={3} sx={{ p: 4 }}>
    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }} color="textPrimary">
      Customer Payments
    </Typography>
    <CustomerPaymentsTable refreshKey={refreshKey} />
  </Paper>
);

const WithdrawalTransactionsTab = ({ refreshKey }) => (
  <Paper elevation={3} sx={{ p: 4 }}>
    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }} color="textPrimary">
      Withdrawal History
    </Typography>
    <WithdrawalHistoryTable refreshKey={refreshKey} />
  </Paper>
);

const PaymentLinksTab = ({ refreshKey }) => (
  <Paper elevation={3} sx={{ p: 4 }}>
    <PaymentLinksPage refreshKey={refreshKey} />
  </Paper>
);

export default PaymentsPage;